import React, { useRef, useState, useEffect, forwardRef } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text, isWeb } from "@xsolla/xui-primitives";
import {
  useId,
  useResolvedTheme,
  type ThemeOverrideProps,
} from "@xsolla/xui-core";
import { Image, TrashCan } from "@xsolla/xui-icons-base";
import { Spinner } from "@xsolla/xui-spinner";

export type ImageUploaderSize = "xl" | "lg" | "md" | "sm" | "xs";

/** Controlled value shape. */
export interface ImageUploaderValue {
  filename?: string;
  url?: string;
}

/**
 * Normalized file shape produced by the platform picker / drag-drop pipeline.
 * - `uri` is a data-URL on web and a file URI on native.
 * - On web, the original `File` is passed through for consumers that need it
 *   (e.g. to upload via FormData / fetch).
 */
export type ImageUploaderFile = {
  name: string;
  size: number;
  uri: string;
  mimeType?: string;
  /** The original DOM File (web only) */
  file?: File;
};

export interface ImageUploaderProps extends ThemeOverrideProps {
  /** Size of the uploader. Figma default is `xl`. */
  size?: ImageUploaderSize;
  /** Placeholder shown under the icon. Accepts a string or a custom React node. */
  placeholder?: React.ReactNode;
  /** Placeholder shown while the file is uploading. Accepts a string or a custom React node. */
  uploadingPlaceholder?: React.ReactNode;
  /** Description below the placeholder. Only rendered when `wideView` is true. Accepts a string or a custom React node. */
  description?: React.ReactNode;
  /** Error message — when provided, component renders in the error state. */
  errorMessage?: string;
  /** Wide view (horizontal layout). When true, the box stretches to its parent's full width. */
  wideView?: boolean;
  /** Disabled state. */
  disabled?: boolean;
  /** Controlled loading state (shows spinner + "Uploading" label). */
  loading?: boolean;

  /** Controlled value. When provided, the component reflects this value. */
  value?: ImageUploaderValue | null;

  /**
   * Fires when the user picks (or drops) a file. Use this to perform the
   * actual upload — the component itself does no I/O.
   *
   * If the handler returns a `Promise`:
   * - The component automatically shows the uploading state (spinner +
   *   `uploadingPlaceholder`) until the promise settles.
   * - If the promise resolves to an `ImageUploaderValue` (`{url, filename?}`)
   *   or a `string` URL, the component automatically calls `onChange(value)`
   *   with that value. The string form is sugar for `{url: <string>}`.
   * - If the promise rejects, the component calls `onChange(null, error)`.
   *
   * For finer-grained control, pass `loading` explicitly instead.
   */
  onUpload?: (
    file: ImageUploaderFile
  ) => void | Promise<ImageUploaderValue | string | void>;
  /**
   * Fires when the displayed value changes — on file pick (with a local
   * data-URL preview), after upload resolves (with the server value), or on
   * remove (`null`). The optional second argument carries any error thrown by
   * the consumer's `onUpload`.
   */
  onChange?: (value: ImageUploaderValue | null, error?: Error) => void;
  /** Fires when the user removes the image (trash click / clear). */
  onDelete?: () => void;

  /**
   * Accepted file types — passed through to the hidden `<input accept>` as a
   * picker hint (web only — ignored on native). Standard HTML syntax:
   * comma-separated list of MIME types (`image/png`), MIME wildcards
   * (`image/*`), or file extensions (`.png`). Defaults to `image/*`.
   *
   * No runtime validation is performed — the consumer's `onUpload` (or the
   * backend it calls) is responsible for accepting/rejecting files.
   */
  accept?: string;
  /**
   * Native file picker hook. Required on native (no DOM `<input type="file">`).
   * On web, omit this and the component falls back to a hidden file input.
   */
  openPicker?: () => Promise<ImageUploaderFile | null>;
  testID?: string;
}

type InternalState =
  | "default"
  | "hover"
  | "focus"
  | "uploading"
  | "uploaded"
  | "uploadedHover"
  | "error"
  | "disable";

export const ImageUploader = forwardRef<HTMLInputElement, ImageUploaderProps>(
  (
    {
      size = "xl",
      placeholder = "Upload",
      uploadingPlaceholder = "Uploading",
      description,
      errorMessage,
      wideView = false,
      accept = "image/*",
      openPicker,
      value,
      onUpload,
      onChange,
      onDelete,
      disabled = false,
      loading = false,
      testID,
      themeMode,
      themeProductContext,
    },
    ref
  ) => {
    const { theme } = useResolvedTheme({ themeMode, themeProductContext });
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isControlled = value !== undefined;
    const [internalPreview, setInternalPreview] = useState<string | null>(null);
    const [isAutoUploading, setIsAutoUploading] = useState(false);
    // Captures errors thrown by the consumer's `onUpload`. Surfaced via the
    // component's normal error UI when no explicit `errorMessage` prop is set.
    const [autoError, setAutoError] = useState<string | null>(null);
    const [isHover, setIsHover] = useState(false);
    const [isFocus, setIsFocus] = useState(false);
    const [isPreviewHover, setIsPreviewHover] = useState(false);
    // Touch / native devices have no hover, so the trash overlay would never
    // appear. Detect once on mount and force `uploadedHover` for those cases.
    const [isHoverless] = useState(
      () =>
        !isWeb ||
        (typeof window !== "undefined" &&
          !!window.matchMedia?.("(hover: none)").matches)
    );

    const reactId = useId();
    const baseId = `image-uploader-${reactId.replace(/[^a-zA-Z0-9-]/g, "")}`;
    const descriptionId = `${baseId}-description`;
    const errorId = `${baseId}-error`;

    const preview = isControlled ? (value?.url ?? null) : internalPreview;

    useEffect(() => {
      if (!isControlled && value === null) setInternalPreview(null);
    }, [isControlled, value]);

    React.useImperativeHandle(
      ref,
      () => fileInputRef.current as HTMLInputElement,
      []
    );

    const sizeStyles = theme.sizing.imageUploader(size);
    const inputColors = theme.colors.control.input;
    const focusColors = theme.colors.control.focus;
    const alertBorder = theme.colors.control.alert.border;
    const alertText = theme.colors.content.alert.primary;
    const scrim = theme.colors.layer.scrim;

    // Resolved error message: explicit prop wins, fall back to caught upload error.
    const resolvedError = errorMessage || autoError;
    const hasError = !!resolvedError;

    let state: InternalState;
    if (disabled) state = "disable";
    else if (loading || isAutoUploading) state = "uploading";
    else if (hasError) state = "error";
    else if (preview)
      state = isPreviewHover || isHoverless ? "uploadedHover" : "uploaded";
    else if (isFocus) state = "focus";
    else if (isHover) state = "hover";
    else state = "default";

    const emitFile = (img: ImageUploaderFile) => {
      // Clear any prior error from a previous upload attempt.
      setAutoError(null);
      if (!isControlled) setInternalPreview(img.uri);
      onChange?.({
        filename: img.name,
        url: img.uri,
      });
      const result = onUpload?.(img);
      // If the consumer's onUpload returns a Promise:
      // - show the uploading state until it settles
      // - on resolve with a value, treat it as the canonical onChange payload
      // - on reject, surface the error internally AND via onChange(null, error)
      if (result && typeof (result as Promise<unknown>).then === "function") {
        setIsAutoUploading(true);
        Promise.resolve(result)
          .then((uploaded) => {
            // Accept either an ImageUploaderValue ({url, filename?}) or a
            // bare string URL as sugar for {url: <string>}.
            let next: ImageUploaderValue | null = null;
            if (typeof uploaded === "string") {
              next = { url: uploaded };
            } else if (
              uploaded &&
              typeof uploaded === "object" &&
              "url" in uploaded
            ) {
              next = uploaded as ImageUploaderValue;
            }
            if (next) {
              if (!isControlled) setInternalPreview(next.url ?? null);
              onChange?.(next);
            }
          })
          .catch((err: Error) => {
            // Surface internally so the error UI lights up without the
            // consumer having to wire up local state.
            setAutoError(err?.message || "Upload failed");
            // Reset preview so we don't keep showing the failed pick.
            if (!isControlled) setInternalPreview(null);
            onChange?.(null, err);
          })
          .finally(() => setIsAutoUploading(false));
      }
    };

    const handleWebFile = (file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const uri = e.target?.result as string;
        // Mutate the DOM File so it satisfies BOTH the DOM File interface (for
        // direct use in FormData / fetch / .size checks) and ImageUploaderFile
        // (with .uri / .mimeType added). `.file` self-references for back-compat
        // with consumers that previously dereferenced `imageFile.file`.
        const enriched = file as File & ImageUploaderFile;
        (enriched as { uri?: string }).uri = uri;
        (enriched as { mimeType?: string }).mimeType = file.type;
        (enriched as { file?: File }).file = enriched;
        emitFile(enriched);
      };
      reader.readAsDataURL(file);
    };

    const handleClick = async () => {
      if (disabled || loading) return;
      if (preview) {
        removeImage();
        return;
      }
      if (openPicker) {
        const picked = await openPicker();
        if (picked) emitFile(picked);
        return;
      }
      if (isWeb) {
        fileInputRef.current?.click();
      }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (file) handleWebFile(file);
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !loading && !preview) setIsHover(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsHover(false);
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsHover(false);
      if (disabled || loading || preview) return;
      const file = e.dataTransfer.files?.[0];
      if (file) handleWebFile(file);
    };

    const removeImage = (e?: { stopPropagation?: () => void }) => {
      e?.stopPropagation?.();
      setAutoError(null);
      if (!isControlled) setInternalPreview(null);
      setIsPreviewHover(false);
      onDelete?.();
      onChange?.(null);
      if (isWeb && fileInputRef.current) fileInputRef.current.value = "";
    };

    let backgroundColor: string = inputColors.bg;
    let borderColor: string = inputColors.border;
    let borderStyle: "dashed" | "solid" = "dashed";
    let labelColor: string = inputColors.text;
    let descriptionColor: string = inputColors.placeholder;
    let iconColor: string = inputColors.text;

    switch (state) {
      case "hover":
        backgroundColor = inputColors.bgHover;
        borderColor = inputColors.borderHover;
        break;
      case "focus":
      case "uploading":
        backgroundColor = focusColors.bg;
        borderColor = focusColors.border;
        break;
      case "uploaded":
      case "uploadedHover":
        backgroundColor = "transparent";
        borderColor = "transparent";
        break;
      case "error":
        backgroundColor = inputColors.bg;
        borderColor = alertBorder;
        borderStyle = "solid";
        labelColor = alertText;
        iconColor = alertText;
        break;
      case "disable":
        backgroundColor = inputColors.bgDisable;
        borderColor = inputColors.borderDisable;
        labelColor = inputColors.textDisable;
        descriptionColor = inputColors.textDisable;
        iconColor = inputColors.textDisable;
        break;
    }

    const rootGap = state === "error" ? sizeStyles.errorGap : 0;
    const placeholderContent =
      state === "uploading" ? uploadingPlaceholder : placeholder;

    const renderInner = () => {
      if (state === "uploaded" || state === "uploadedHover") {
        return (
          <>
            <Box
              as="img"
              src={preview || undefined}
              alt={value?.filename ?? "Uploaded image"}
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              borderRadius={sizeStyles.radius}
              resizeMode="cover"
              {...(isWeb && { width: "100%", height: "100%" })}
              style={isWeb ? { objectFit: "cover" } : undefined}
            />
            {state === "uploadedHover" && !disabled && (
              <>
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  backgroundColor={scrim}
                  borderRadius={sizeStyles.radius}
                  {...(isWeb && { "aria-hidden": "true" })}
                />
                <Box
                  position="relative"
                  width={sizeStyles.iconSize}
                  height={sizeStyles.iconSize}
                  alignItems="center"
                  justifyContent="center"
                  zIndex={1}
                  pointerEvents="none"
                  {...(isWeb && { "aria-hidden": "true" })}
                >
                  <TrashCan
                    size={sizeStyles.iconSize}
                    color={theme.colors.content.static.light}
                  />
                </Box>
              </>
            )}
          </>
        );
      }

      return (
        <>
          {state === "uploading" ? (
            <Spinner
              size={sizeStyles.spinnerSize}
              color={theme.colors.content.brand.primary}
              {...(isWeb && { "aria-hidden": "true" })}
            />
          ) : (
            <Box {...(isWeb && { "aria-hidden": "true" })}>
              <Image size={sizeStyles.iconSize} color={iconColor} />
            </Box>
          )}

          {placeholderContent &&
            (typeof placeholderContent === "string" ? (
              <Text
                data-testid="image-uploader__placeholder"
                color={labelColor}
                fontSize={sizeStyles.labelFontSize}
                lineHeight={sizeStyles.labelLineHeight}
                fontWeight="500"
                textAlign="center"
                numberOfLines={1}
                style={
                  isWeb
                    ? {
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }
                    : undefined
                }
              >
                {placeholderContent}
              </Text>
            ) : (
              <Box data-testid="image-uploader__placeholder">
                {placeholderContent}
              </Box>
            ))}

          {description &&
            wideView &&
            state !== "error" &&
            (typeof description === "string" ? (
              <Text
                data-testid="image-uploader__description"
                color={descriptionColor}
                fontSize={sizeStyles.descriptionFontSize}
                lineHeight={sizeStyles.descriptionLineHeight}
                textAlign="center"
                numberOfLines={1}
                style={
                  isWeb
                    ? {
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }
                    : undefined
                }
                {...(isWeb && { id: descriptionId })}
              >
                {description}
              </Text>
            ) : (
              <Box
                data-testid="image-uploader__description"
                {...(isWeb && { id: descriptionId })}
              >
                {description}
              </Box>
            ))}
        </>
      );
    };

    // Web-only DOM event handlers — stripped on native.
    const webOnlyHandlers = isWeb
      ? {
          onMouseEnter: () => {
            if (disabled || loading) return;
            if (preview) setIsPreviewHover(true);
            else setIsHover(true);
          },
          onMouseLeave: () => {
            if (preview) setIsPreviewHover(false);
            else setIsHover(false);
          },
          onFocus: (e: React.FocusEvent<HTMLElement>) => {
            if (disabled || loading || preview) return;
            // Only show the focus visual state for keyboard focus, not for
            // pointer focus (e.g. after the native file picker is cancelled).
            if (
              typeof e.target?.matches === "function" &&
              !e.target.matches(":focus-visible")
            ) {
              return;
            }
            setIsFocus(true);
          },
          onBlur: () => setIsFocus(false),
          onDragOver: handleDragOver,
          onDragLeave: handleDragLeave,
          onDrop: handleDrop,
        }
      : {};

    // Web-only CSS props — dropped on native to avoid style warnings.
    const webOnlyStyle = isWeb
      ? {
          boxSizing: "border-box" as const,
          display: "flex" as const,
          cursor: (disabled || loading
            ? "not-allowed"
            : "pointer") as React.CSSProperties["cursor"],
          outline: "none",
          transition: "background-color 0.15s ease, border-color 0.15s ease",
        }
      : {};

    // aria-label needs a string; fall back to defaults when placeholder/uploadingPlaceholder
    // are passed as React nodes.
    const buttonAriaLabel = preview
      ? `Remove image${value?.filename ? `: ${value.filename}` : ""}`
      : state === "uploading"
        ? typeof uploadingPlaceholder === "string"
          ? uploadingPlaceholder
          : "Uploading"
        : typeof placeholder === "string"
          ? placeholder
          : "Upload";

    const describedBy =
      [
        state === "error" && resolvedError ? errorId : null,
        description && wideView && state !== "error" ? descriptionId : null,
      ]
        .filter(Boolean)
        .join(" ") || undefined;

    return (
      <Box
        testID={testID || "image-uploader"}
        flexDirection="column"
        gap={rootGap}
        alignItems="flex-start"
        width={wideView ? "100%" : undefined}
      >
        {isWeb && (
          <input
            type="file"
            ref={fileInputRef}
            accept={accept}
            onChange={handleFileChange}
            style={{ display: "none" }}
            disabled={disabled}
            tabIndex={-1}
            aria-hidden="true"
            data-testid="image-uploader__input"
          />
        )}

        <Box
          as={isWeb ? "button" : "div"}
          disabled={disabled || loading}
          data-testid="image-uploader__button"
          onPress={handleClick}
          {...(isWeb && {
            "aria-label": buttonAriaLabel,
            "aria-busy": loading || undefined,
            "aria-invalid": state === "error" || undefined,
            "aria-describedby": describedBy,
          })}
          {...webOnlyHandlers}
          position="relative"
          width={wideView ? "100%" : sizeStyles.box}
          height={sizeStyles.box}
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={sizeStyles.gap}
          padding={state === "uploaded" ? 0 : sizeStyles.padding}
          borderWidth={sizeStyles.borderWidth}
          borderStyle={borderStyle}
          borderColor={borderColor}
          borderRadius={sizeStyles.radius}
          backgroundColor={backgroundColor}
          overflow="hidden"
          style={webOnlyStyle}
        >
          {renderInner()}
        </Box>

        {state === "error" && resolvedError && (
          <Text
            data-testid="image-uploader__error"
            color={alertText}
            fontSize={sizeStyles.errorFontSize}
            lineHeight={sizeStyles.errorLineHeight}
            {...(isWeb && { id: errorId, role: "alert" })}
          >
            {resolvedError}
          </Text>
        )}
      </Box>
    );
  }
);

ImageUploader.displayName = "ImageUploader";
